import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { API_BASE } from "../utils/apiBase";

const BASE_URL = API_BASE;

const EVENT_LABELS = {
  trip: { label: "Trip / Vacation", icon: "✈" },
  concert: { label: "Concert / Show", icon: "🎵" },
  wedding: { label: "Wedding", icon: "💍" },
  graduation_party: { label: "Graduation Party", icon: "🎓" },
  holiday_gathering: { label: "Holiday Gathering", icon: "🎄" },
  game_night: { label: "Game Night", icon: "🎲" },
  anniversary_dinner: { label: "Anniversary", icon: "🕯" },
  baby_shower: { label: "Baby Shower", icon: "🍼" },
  birthday_party: { label: "Birthday Party", icon: "🎂" },
  other_event: { label: "Event", icon: "📅" },
};

function RsvpModal({ event, onClose, onDone }) {
  const [form, setForm] = useState({ name: "", status: "in", guest_count: 1, dietary_notes: "", note: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async e => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const token = localStorage.getItem("access_token");
      const headers = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;
      const res = await fetch(`${BASE_URL}/events/public/${event.share_slug}/rsvp`, {
        method: "POST", headers, body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to submit");
      onDone(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(28,25,23,0.45)",
      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, padding: 20,
    }}>
      <div style={{
        background: "var(--warm-white)", borderRadius: "var(--border-radius-lg)",
        border: "1px solid var(--color-border-tertiary)", padding: 32,
        width: "100%", maxWidth: 400,
      }}>
        <h3 style={{ fontSize: 18, fontFamily: "var(--font-display)", fontWeight: 300, marginBottom: 4 }}>
          RSVP for {event.title || event.destination || "this event"}
        </h3>
        <p style={{ fontSize: 14, color: "var(--ink-muted)", marginBottom: 20 }}>
          Let the organiser know if you're coming.
        </p>

        {error && (
          <div style={{ background: "var(--rose-light)", color: "var(--rose-dark)", padding: "10px 14px", borderRadius: "var(--radius-sm)", fontSize: 14, marginBottom: 14 }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Your name *</label>
            <input className="form-input" value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="Your name" required autoFocus />
          </div>

          <div className="form-group">
            <label className="form-label">Are you coming?</label>
            <div style={{ display: "flex", gap: 8 }}>
              {[
                { value: "in", label: "✓ I'm in!" },
                { value: "maybe", label: "Maybe" },
                { value: "out", label: "Can't make it" },
              ].map(opt => (
                <button key={opt.value} type="button"
                  onClick={() => setForm(f => ({ ...f, status: opt.value }))}
                  style={{
                    flex: 1, padding: "8px 4px", borderRadius: "var(--radius-sm)",
                    fontSize: 13, fontWeight: 500, cursor: "pointer",
                    border: "1px solid",
                    borderColor: form.status === opt.value ? "var(--rose)" : "var(--border)",
                    background: form.status === opt.value ? "var(--rose-light)" : "var(--warm-white)",
                    color: form.status === opt.value ? "var(--rose-dark)" : "var(--ink-soft)",
                  }}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Note (optional)</label>
            <input className="form-input" value={form.note}
              onChange={e => setForm(f => ({ ...f, note: e.target.value }))}
              placeholder="e.g. Arriving late, bringing a +1…" />
          </div>

          <div className="form-group">
            <label className="form-label">Guests</label>
            <input className="form-input" type="number" min="1" max="20" value={form.guest_count}
              onChange={e => setForm(f => ({ ...f, guest_count: e.target.value }))}
              placeholder="1" />
          </div>

          <div className="form-group">
            <label className="form-label">Dietary notes (optional)</label>
            <input className="form-input" value={form.dietary_notes}
              onChange={e => setForm(f => ({ ...f, dietary_notes: e.target.value }))}
              placeholder="Allergies, vegetarian, no pork…" />
          </div>

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? <><span className="spinner" /> Sending…</> : "Send RSVP"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function PublicEventPage() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showRsvp, setShowRsvp] = useState(false);
  const [myRsvp, setMyRsvp] = useState(null);
  const [viewerRsvp, setViewerRsvp] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("access_token");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    fetch(`${BASE_URL}/events/public/${slug}`, { headers })
      .then(r => r.json())
      .then(data => {
        if (data.error) throw new Error(data.error);
        setEvent(data);
        if (data.viewer_rsvp) {
          setViewerRsvp(data.viewer_rsvp);
          setMyRsvp(data.viewer_rsvp.status);
        }
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [slug]);

  const handleRsvpDone = (data) => {
    setMyRsvp(data.status);
    setViewerRsvp(data.viewer_rsvp || data.rsvp || null);
    setShowRsvp(false);
    setEvent(ev => ({
      ...ev,
      rsvp_in: data.rsvp_in,
      rsvp_maybe: data.rsvp_maybe,
      rsvp_out: data.rsvp_out,
      guest_count: data.guest_count,
      rsvps: viewerAccess === "workspace_member"
        ? [...(ev.rsvps || []), data.rsvp]
        : (ev.rsvps || []),
    }));
  };

  if (loading) return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 14, background: "var(--cream)" }}>
      <span className="spinner" style={{ width: 22, height: 22, borderWidth: 3, borderColor: "var(--border-strong)", borderTopColor: "var(--rose)" }} />
      <div style={{ fontSize: 15, color: "var(--ink-muted)" }}>Loading event…</div>
    </div>
  );

  if (error) return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--cream)" }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>📅</div>
        <h2 style={{ fontFamily: "var(--font-display)", fontWeight: 300, marginBottom: 8 }}>Event not found</h2>
        <p style={{ color: "var(--ink-muted)" }}>This event may have been removed or the link has changed.</p>
      </div>
    </div>
  );

  const meta = EVENT_LABELS[event.event_type] || EVENT_LABELS.other_event;
  const viewerAccess = event.viewer_access || (user ? "guest" : "public");
  const dateStr = new Date(event.event_date + "T00:00:00").toLocaleDateString("en-US", {
    weekday: "long", month: "long", day: "numeric", year: "numeric",
  });
  const endDateStr = event.end_date
    ? new Date(event.end_date + "T00:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric" })
    : null;

  return (
    <div style={{ minHeight: "100vh", background: "var(--cream)" }}>
      {/* Header */}
      <div style={{ background: "var(--warm-white)", borderBottom: "1px solid var(--border)", padding: "32px 24px", textAlign: "center" }}>
        <div style={{ maxWidth: 640, margin: "0 auto" }}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>{meta.icon}</div>
          <div style={{ fontSize: 13, color: "var(--rose)", fontWeight: 500, marginBottom: 8, letterSpacing: "0.05em" }}>
            {meta.label.toUpperCase()}
          </div>
          <h1 style={{ fontFamily: "var(--font-display)", fontSize: "clamp(24px, 5vw, 36px)", fontWeight: 300, marginBottom: 8 }}>
            {event.title || event.destination || meta.label}
          </h1>
          <p style={{ fontSize: 15, color: "var(--ink-muted)", marginBottom: 4 }}>
            Organised by {event.group_emoji} {event.group_name}
          </p>
          <p style={{ fontSize: 14, color: "var(--ink-hint)" }}>
            {dateStr}{endDateStr ? ` → ${endDateStr}` : ""}
          </p>
          {event.destination && event.title && (
            <p style={{ fontSize: 14, color: "var(--ink-soft)", marginTop: 4 }}>📍 {event.destination}</p>
          )}
          {event.location && (
            <p style={{ fontSize: 14, color: "var(--ink-soft)", marginTop: 4 }}>📍 {event.location}</p>
          )}
          {event.notes && (
            <p style={{ fontSize: 15, color: "var(--ink-soft)", marginTop: 12, fontStyle: "italic", fontFamily: "var(--font-display)", lineHeight: 1.6, maxWidth: 480, margin: "12px auto 0" }}>
              "{event.notes}"
            </p>
          )}
          {viewerAccess === "workspace_member" ? (
            <div style={{
              marginTop: 16,
              padding: "12px 14px",
              borderRadius: "var(--radius-sm)",
              background: "var(--cream)",
              border: "1px solid var(--border)",
              fontSize: 13,
              color: "var(--ink-soft)",
              textAlign: "left",
            }}>
              <strong>You're viewing this as a workspace member.</strong>{" "}
              {event.event_scope === "personal"
                ? "Use Events & Trips to manage private planning and review guest responses."
                : "Use shared planning to manage RSVPs, plans, and linked registry details from inside your workspace."}
            </div>
          ) : null}

        </div>
      </div>

      <div style={{ maxWidth: 640, margin: "0 auto", padding: "32px 24px" }}>

        {/* RSVP action */}
        {viewerAccess === "workspace_member" ? (
          <div style={{
            background: "var(--warm-white)", border: "1px solid var(--border)",
            borderRadius: "var(--radius-md)", padding: "20px 24px",
            display: "flex", alignItems: "center", justifyContent: "space-between",
            gap: 16, flexWrap: "wrap", marginBottom: 24,
          }}>
            <div>
              <p style={{ fontSize: 15, fontWeight: 500, color: "var(--ink)", margin: 0 }}>
                {event.event_scope === "personal" ? "Manage this event in Events & Trips" : "Manage this event in shared planning"}
              </p>
              <p style={{ fontSize: 13, color: "var(--ink-muted)", margin: "4px 0 0" }}>
                {event.event_scope === "personal"
                  ? "Review guest responses and keep the private plan in your workspace."
                  : "Review guest responses and manage the full event from the Family shared space."}
              </p>
            </div>
            <a
              href={event.event_scope === "personal" ? `/app/events?event_id=${event.id}` : (event.group_id ? `/groups?group_id=${event.group_id}&event_id=${event.id}` : "/groups")}
              className="btn btn-primary"
            >
              {event.event_scope === "personal" ? "Open Events & Trips" : "Open shared planning"}
            </a>
          </div>
        ) : myRsvp ? (
          <div style={{ textAlign: "center", padding: "24px 0", borderBottom: "1px solid var(--border)", marginBottom: 24 }}>
            <div style={{ fontSize: 36, marginBottom: 8 }}>
              {myRsvp === "in" ? "🎉" : myRsvp === "maybe" ? "🤔" : "😢"}
            </div>
            <p style={{ fontSize: 16, fontWeight: 500, color: "var(--ink)" }}>
              {myRsvp === "in" ? "You're in! See you there." : myRsvp === "maybe" ? "Maybe — you'll let them know." : "Can't make it — you've been noted."}
            </p>
          </div>
        ) : (
          <div style={{
            background: "var(--warm-white)", border: "1px solid var(--border)",
            borderRadius: "var(--radius-md)", padding: "20px 24px",
            display: "flex", alignItems: "center", justifyContent: "space-between",
            gap: 16, flexWrap: "wrap", marginBottom: 24,
          }}>
            <div>
              <p style={{ fontSize: 15, fontWeight: 500, color: "var(--ink)", margin: 0 }}>Are you coming?</p>
              <p style={{ fontSize: 13, color: "var(--ink-muted)", margin: "4px 0 0" }}>
                Let the organiser know so they can plan.
              </p>
            </div>
            <button className="btn btn-primary" onClick={() => setShowRsvp(true)}>
              RSVP now
            </button>
          </div>
        )}

        {viewerAccess !== "workspace_member" && viewerRsvp && (
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink-soft)", marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Your RSVP
            </div>
            <span style={{
              display: "inline-flex",
              alignItems: "center",
              padding: "4px 12px",
              background: viewerRsvp.status === "in" ? "var(--green-light)" : viewerRsvp.status === "maybe" ? "var(--gold-light)" : "var(--cream)",
              color: viewerRsvp.status === "in" ? "var(--green)" : viewerRsvp.status === "maybe" ? "#8B6914" : "var(--ink-muted)",
              borderRadius: 20,
              fontSize: 13,
              fontWeight: 500,
            }}>
              {viewerRsvp.status === "in" ? "✓ Coming" : viewerRsvp.status === "maybe" ? "Maybe" : "Can't make it"}
              {viewerRsvp.note ? ` — ${viewerRsvp.note}` : ""}
            </span>
          </div>
        )}

        {/* Linked registry */}
        {event.registry_slug && (
          <div style={{
            background: "var(--warm-white)", border: "1px solid var(--border)",
            borderRadius: "var(--radius-md)", padding: "16px 20px",
            display: "flex", alignItems: "center", justifyContent: "space-between",
            gap: 16, flexWrap: "wrap", marginBottom: 24,
          }}>
            <div>
              <p style={{ fontSize: 14, fontWeight: 500, color: "var(--ink)", margin: 0 }}>
                🎁 {event.registry_title || "Gift registry"}
              </p>
              <p style={{ fontSize: 13, color: "var(--ink-muted)", margin: "2px 0 0" }}>
                View and reserve gifts for this event.
              </p>
            </div>
            <a href={`/registry/${event.registry_slug}`} className="btn btn-secondary btn-sm">
              View registry →
            </a>
          </div>
        )}

        {/* Guest CTA */}
        {!user && (
          <div style={{
            background: "var(--warm-white)", border: "1px solid var(--border)",
            borderRadius: "var(--radius-md)", padding: "16px 20px",
            display: "flex", alignItems: "center", justifyContent: "space-between",
            gap: 16, flexWrap: "wrap", marginBottom: 24,
          }}>
            <p style={{ fontSize: 14, color: "var(--ink-muted)", margin: 0 }}>
              Create a free account to save this RSVP link and respond as yourself. This still does not give access to private event planning.
            </p>
            <a href={`/register?from=event&ref=${slug}`} className="btn btn-primary btn-sm" style={{ whiteSpace: "nowrap" }}>
              Create free account
            </a>
          </div>
        )}

        <p style={{ fontSize: 12, color: "var(--ink-hint)", textAlign: "center", marginTop: 24 }}>
          Powered by <span style={{ color: "var(--rose)", fontFamily: "var(--font-display)" }}>HouseMind</span>
        </p>
      </div>

      {showRsvp && <RsvpModal event={event} onClose={() => setShowRsvp(false)} onDone={handleRsvpDone} />}
    </div>
  );
}
